import type { FC } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowRight, CheckCircle2, FlaskConical } from 'lucide-react';
import type { PageOfSampleTestDto } from '@/api';
import { Badge, Card, CardHeader, CardTitle, Empty, Skeleton } from '@/ui';

import { formatDate, toNumber } from '../../lib/format';
import { DUE_SAMPLE_TESTS_LIMIT } from './api/queries';

/*
 * «Узнать результат теста» - задачи по тестам образцов, отдельно от задач по
 * отгрузкам: тот список типизирован под отгрузку. Пора - решает backend (тест
 * идёт, дата теста сегодня или прошла). Строка ведёт в план тестирования с
 * фильтрами «Пора звонить» и клиентом строки: звонок - клиенту, сразу обо всех
 * его образцах. Задачи - всей команде, как и остальные на этом экране.
 */
export const DueSampleTests: FC<{
  page: PageOfSampleTestDto | undefined;
  isPending: boolean;
}> = ({ page, isPending }) => {
  const tests = page?.items ?? [];
  const total = toNumber(page?.totalRecords ?? 0);

  return (
    <Card className="mb-[22px]">
      <CardHeader>
        <div className="flex items-center gap-2.5">
          <Badge tone="approval" className="h-[26px] px-2.5 text-xs">
            <FlaskConical aria-hidden className="size-3" />
            {total}
          </Badge>
          <CardTitle>Узнать результат теста</CardTitle>
        </div>
        <Link
          to="/sample-tests"
          search={{ due: true }}
          className="inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-xs font-[550] whitespace-nowrap text-ink-700 hover:bg-ink-100"
        >
          План тестирования <ArrowRight aria-hidden className="size-3" />
        </Link>
      </CardHeader>

      {isPending && (
        <div className="flex flex-col gap-3 px-[18px] py-4">
          {Array.from({ length: 2 }, (_, index) => (
            <Skeleton key={index} className="h-9" />
          ))}
        </div>
      )}

      {!isPending && tests.length === 0 && (
        <Empty
          className="py-8"
          icon={<CheckCircle2 aria-hidden className="size-7" />}
        >
          Сегодня узнавать результат не у кого
        </Empty>
      )}

      {tests.map((test) => (
        <div
          key={test.id}
          data-row-id={test.id}
          className="relative flex items-center gap-3.5 border-b border-ink-150 px-4 py-3 last:border-b-0 hover:bg-ink-50 has-[a:focus-visible]:bg-ink-50"
        >
          <span className="grid size-7 shrink-0 place-items-center rounded-[7px] bg-st-approval-bg text-st-approval">
            <FlaskConical aria-hidden className="size-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <Link
              to="/sample-tests"
              search={{ due: true, partner: test.partner.id }}
              className="block truncate text-[13px] font-[550] text-ink-900 outline-none after:absolute after:inset-0 after:content-['']"
            >
              {test.partner.name} · {test.product.name}
            </Link>
            <div className="mt-0.5 truncate text-xs text-ink-500">
              Отгрузка образцов №{' '}
              <span className="font-mono">{test.shipment.number}</span> от{' '}
              {formatDate(test.shipment.date)} ·{' '}
              <span className="font-mono">{test.product.code}</span>
            </div>
          </div>
          <span className="w-[150px] shrink-0 text-xs font-[550] text-st-delayed">
            Дата теста: {formatDate(test.testDate)}
          </span>
          {/* Вид кнопки макета; ведёт сама строка - ссылка на план тестирования. */}
          <span aria-hidden className="flex w-[110px] shrink-0 justify-end">
            <span className="inline-flex h-7 items-center gap-1.5 rounded-md border border-ink-200 bg-white px-2.5 text-xs font-[550] text-ink-800">
              Открыть <ArrowRight className="size-[11px]" />
            </span>
          </span>
        </div>
      ))}

      {total > DUE_SAMPLE_TESTS_LIMIT && (
        <div className="border-t border-ink-150 bg-ink-50 px-4 py-2.5 text-xs text-ink-500">
          Показаны {tests.length} из {total} - остальные в{' '}
          <Link
            to="/sample-tests"
            search={{ due: true }}
            className="font-[550] text-ink-700 underline-offset-2 hover:underline"
          >
            плане тестирования
          </Link>
        </div>
      )}
    </Card>
  );
};
